import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { 
  Cloud, 
  HardDrive, 
  FolderOpen, 
  File, 
  Download, 
  Trash2, 
  RefreshCw, 
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Database,
  BarChart3,
  Shield,
  Settings,
  Archive,
  Loader2,
  ExternalLink,
  ChevronRight,
  ArrowLeft,
  FileText,
  Image,
  FileSpreadsheet,
  Eye
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// API helpers
const s3API = {
  getConfig: () => axios.get(`${API}/api/admin/s3/config`),
  getStatus: () => axios.get(`${API}/api/admin/s3/status`),
  testConnection: () => axios.post(`${API}/api/admin/s3/test-connection`),
  getStatistics: () => axios.get(`${API}/api/admin/s3/statistics`),
  getMonthlyStats: (months = 6) => axios.get(`${API}/api/admin/s3/statistics/monthly?months=${months}`),
  listFiles: (prefix = '', maxKeys = 100) => axios.get(`${API}/api/admin/s3/files?prefix=${encodeURIComponent(prefix)}&max_keys=${maxKeys}`),
  searchFiles: (query) => axios.get(`${API}/api/admin/s3/files/search?query=${encodeURIComponent(query)}`),
  getFileUrl: (fileId, expiration = 300) => axios.get(`${API}/api/admin/s3/files/${fileId}/url?expiration=${expiration}`),
  deleteFile: (fileId) => axios.delete(`${API}/api/admin/s3/files/${fileId}`),
  createBackup: (prefix = '') => axios.post(`${API}/api/admin/s3/backup/create?prefix=${encodeURIComponent(prefix)}`),
  listBackups: () => axios.get(`${API}/api/admin/s3/backup/list`),
  findOrphaned: () => axios.get(`${API}/api/admin/s3/orphaned-files`),
  cleanupOrphaned: (dryRun = true) => axios.post(`${API}/api/admin/s3/cleanup/orphaned?dry_run=${dryRun}`),
  migrateToS3: (limit = 10) => axios.post(`${API}/api/admin/s3/migrate/local-to-s3?limit=${limit}`)
};

// File icon helper
const getFileIcon = (filename, mimeType) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext) || mimeType?.startsWith('image/')) {
    return <Image className="h-5 w-5 text-green-500" />;
  }
  if (['xls', 'xlsx', 'csv'].includes(ext) || mimeType?.includes('spreadsheet')) {
    return <FileSpreadsheet className="h-5 w-5 text-emerald-500" />;
  }
  if (['pdf'].includes(ext) || mimeType === 'application/pdf') {
    return <FileText className="h-5 w-5 text-red-500" />;
  }
  return <File className="h-5 w-5 text-blue-500" />;
};

// Format size helper
const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export default function S3Management() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // State
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [files, setFiles] = useState({ folders: [], files: [] });
  const [currentPrefix, setCurrentPrefix] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [backups, setBackups] = useState([]);
  const [orphanedFiles, setOrphanedFiles] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState([]);
  
  // Loading states
  const [testingConnection, setTestingConnection] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  // Load initial data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, statusRes, statsRes] = await Promise.all([
        s3API.getConfig(),
        s3API.getStatus().catch(() => ({ data: { status: 'error' } })),
        s3API.getStatistics().catch(() => ({ data: null }))
      ]);
      
      setConfig(configRes.data);
      setStatus(statusRes.data);
      setStatistics(statsRes.data);
    } catch (error) {
      console.error('Error loading S3 data:', error);
      toast({ title: 'Error', description: 'No se pudo cargar la configuración de S3', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Test connection
  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const res = await s3API.testConnection();
      if (res.data.success) {
        toast({ title: 'Conexión exitosa', description: res.data.message });
      } else {
        toast({ title: 'Error de conexión', description: res.data.message, variant: 'destructive' });
      }
      await loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo probar la conexión', variant: 'destructive' });
    } finally {
      setTestingConnection(false);
    }
  };

  // Load files
  const loadFiles = async (prefix = '') => {
    try {
      const res = await s3API.listFiles(prefix);
      setFiles(res.data);
      setCurrentPrefix(prefix);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudieron cargar los archivos', variant: 'destructive' });
    }
  };

  // Search files
  const handleSearch = async () => {
    if (!searchQuery || searchQuery.length < 2) return;
    try {
      const res = await s3API.searchFiles(searchQuery);
      setSearchResults(res.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Error en la búsqueda', variant: 'destructive' });
    }
  };

  // Open file
  const handleOpenFile = async (fileId) => {
    try {
      const res = await s3API.getFileUrl(fileId, 300);
      window.open(res.data.url, '_blank');
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo obtener la URL del archivo', variant: 'destructive' });
    }
  };

  // Delete file
  const handleDeleteFile = async (fileId, fileName) => {
    if (!window.confirm(`¿Eliminar "${fileName}"? Esta acción no se puede deshacer.`)) return;
    try {
      await s3API.deleteFile(fileId);
      toast({ title: 'Eliminado', description: 'Archivo eliminado correctamente' });
      if (searchResults.length > 0) {
        setSearchResults(prev => prev.filter(f => f.id !== fileId));
      }
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar el archivo', variant: 'destructive' });
    }
  };

  // Create backup
  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      const res = await s3API.createBackup();
      toast({ 
        title: 'Backup creado', 
        description: `${res.data.summary.total_files} archivos (${res.data.summary.total_size_mb} MB)` 
      });
      loadBackups();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo crear el backup', variant: 'destructive' });
    } finally {
      setCreatingBackup(false);
    }
  };

  // Load backups
  const loadBackups = async () => {
    try {
      const res = await s3API.listBackups();
      setBackups(res.data.backups || []);
    } catch (error) {
      console.error('Error loading backups:', error);
    }
  };

  // Find orphaned files
  const handleFindOrphaned = async () => {
    try {
      const res = await s3API.findOrphaned();
      setOrphanedFiles(res.data);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo analizar archivos huérfanos', variant: 'destructive' });
    }
  };

  // Cleanup orphaned
  const handleCleanupOrphaned = async (dryRun = true) => {
    setCleaning(true);
    try {
      const res = await s3API.cleanupOrphaned(dryRun);
      if (dryRun) {
        toast({ 
          title: 'Análisis completado', 
          description: `Se eliminarían ${res.data.would_delete} archivos` 
        });
      } else {
        toast({ 
          title: 'Limpieza completada', 
          description: `${res.data.deleted} archivos eliminados` 
        });
        handleFindOrphaned();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Error durante la limpieza', variant: 'destructive' });
    } finally {
      setCleaning(false);
    }
  };

  // Migrate to S3
  const handleMigrate = async () => {
    setMigrating(true);
    try {
      const res = await s3API.migrateToS3(20);
      toast({ 
        title: 'Migración completada', 
        description: `${res.data.migrated} archivos migrados. Quedan ${res.data.remaining} pendientes.` 
      });
      await loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'Error durante la migración', variant: 'destructive' });
    } finally {
      setMigrating(false);
    }
  };

  // Load monthly stats
  useEffect(() => {
    if (activeTab === 'statistics') {
      s3API.getMonthlyStats(6).then(res => setMonthlyStats(res.data.monthly || [])).catch(() => {});
    }
  }, [activeTab]);

  // Load files when switching to browser tab
  useEffect(() => {
    if (activeTab === 'browser') {
      loadFiles('');
    }
  }, [activeTab]);

  // Load backups when switching to backup tab
  useEffect(() => {
    if (activeTab === 'backup') {
      loadBackups();
    }
  }, [activeTab]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="s3-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Cloud className="h-6 w-6 text-orange-500" />
            Almacenamiento S3
          </h1>
          <p className="text-muted-foreground">
            Gestión de archivos en la nube de AWS S3
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={status?.status === 'healthy' ? 'default' : 'destructive'}>
            {status?.status === 'healthy' ? (
              <><CheckCircle className="h-3 w-3 mr-1" /> Conectado</>
            ) : (
              <><XCircle className="h-3 w-3 mr-1" /> Desconectado</>
            )}
          </Badge>
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-1" /> Actualizar
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">
            <Settings className="h-4 w-4 mr-1" /> General
          </TabsTrigger>
          <TabsTrigger value="statistics">
            <BarChart3 className="h-4 w-4 mr-1" /> Estadísticas
          </TabsTrigger>
          <TabsTrigger value="browser">
            <FolderOpen className="h-4 w-4 mr-1" /> Explorador
          </TabsTrigger>
          <TabsTrigger value="backup">
            <Archive className="h-4 w-4 mr-1" /> Backups
          </TabsTrigger>
          <TabsTrigger value="maintenance">
            <Shield className="h-4 w-4 mr-1" /> Mantenimiento
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Configuration Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Configuración AWS</CardTitle>
                <CardDescription>Credenciales y configuración del bucket</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Access Key ID</span>
                  <code className="text-sm bg-muted px-2 py-0.5 rounded">{config?.aws_access_key_id}</code>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Secret Key</span>
                  <code className="text-sm bg-muted px-2 py-0.5 rounded">{config?.aws_secret_access_key}</code>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Bucket</span>
                  <code className="text-sm bg-muted px-2 py-0.5 rounded">{config?.aws_bucket_name}</code>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Región</span>
                  <code className="text-sm bg-muted px-2 py-0.5 rounded">{config?.aws_region}</code>
                </div>
                <Button 
                  onClick={handleTestConnection} 
                  disabled={testingConnection}
                  className="w-full mt-4"
                >
                  {testingConnection ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Probar Conexión
                </Button>
              </CardContent>
            </Card>

            {/* Status Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Estado del Servicio</CardTitle>
                <CardDescription>Información de conexión y almacenamiento</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert variant={status?.status === 'healthy' ? 'default' : 'destructive'}>
                  <AlertDescription className="flex items-center gap-2">
                    {status?.status === 'healthy' ? (
                      <><CheckCircle className="h-5 w-5 text-green-500" /> {status?.message}</>
                    ) : (
                      <><XCircle className="h-5 w-5" /> {status?.message || 'No conectado'}</>
                    )}
                  </AlertDescription>
                </Alert>
                
                {statistics && (
                  <div className="grid grid-cols-2 gap-4 pt-4">
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-3xl font-bold text-primary">{statistics.summary.total_files}</div>
                      <div className="text-sm text-muted-foreground">Archivos totales</div>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-3xl font-bold text-orange-500">{statistics.summary.total_size_mb} MB</div>
                      <div className="text-sm text-muted-foreground">Espacio usado</div>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-3xl font-bold text-green-500">{statistics.summary.s3_files}</div>
                      <div className="text-sm text-muted-foreground">En S3</div>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-3xl font-bold text-yellow-500">{statistics.summary.local_files}</div>
                      <div className="text-sm text-muted-foreground">Locales</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* STATISTICS TAB */}
        <TabsContent value="statistics" className="space-y-4">
          {statistics && (
            <>
              {/* By Entity */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Archivos por Entidad</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {Object.entries(statistics.by_entity).map(([entity, data]) => (
                      <div key={entity} className="text-center p-4 border rounded-lg">
                        <div className="text-2xl font-bold">{data.count}</div>
                        <div className="text-sm text-muted-foreground capitalize">{entity}</div>
                        <div className="text-xs text-muted-foreground">{formatSize(data.size)}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* By Type */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Por Tipo de Archivo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {statistics.by_type.slice(0, 8).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-32 text-sm truncate">{item.type?.split('/')[1] || item.type}</div>
                        <div className="flex-1">
                          <Progress 
                            value={(item.count / statistics.summary.total_files) * 100} 
                            className="h-2"
                          />
                        </div>
                        <div className="w-20 text-right text-sm">{item.count} archivos</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Monthly Stats */}
              {monthlyStats.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Actividad Mensual</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-2 h-40">
                      {monthlyStats.map((month, idx) => {
                        const maxCount = Math.max(...monthlyStats.map(m => m.count));
                        const height = maxCount > 0 ? (month.count / maxCount) * 100 : 0;
                        return (
                          <div key={idx} className="flex-1 flex flex-col items-center">
                            <div 
                              className="w-full bg-primary rounded-t"
                              style={{ height: `${height}%`, minHeight: month.count > 0 ? '4px' : '0' }}
                            />
                            <div className="text-xs text-muted-foreground mt-1">
                              {month.month}/{String(month.year).slice(2)}
                            </div>
                            <div className="text-xs font-medium">{month.count}</div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* BROWSER TAB */}
        <TabsContent value="browser" className="space-y-4">
          {/* Search */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Buscar archivos por nombre..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4 mr-1" /> Buscar
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Search Results or File Browser */}
          {searchResults.length > 0 ? (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Resultados de búsqueda ({searchResults.length})</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setSearchResults([])}>
                  Limpiar
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {searchResults.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted">
                      <div className="flex items-center gap-3">
                        {getFileIcon(file.nombre, file.tipo_archivo)}
                        <div>
                          <div className="font-medium">{file.nombre}</div>
                          <div className="text-xs text-muted-foreground">
                            {file.tamano_formatted} • {file.storage === 's3' ? 'S3' : 'Local'}
                            {file.expediente_id && ` • Expediente #${file.expediente_id}`}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenFile(file.id)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteFile(file.id, file.nombre)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  {currentPrefix && (
                    <Button variant="ghost" size="sm" onClick={() => {
                      const parts = currentPrefix.split('/').filter(Boolean);
                      parts.pop();
                      loadFiles(parts.join('/'));
                    }}>
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                  )}
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FolderOpen className="h-5 w-5" />
                    {currentPrefix || 'Raíz del Bucket'}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {/* Folders */}
                {files.folders.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                    {files.folders.map((folder) => (
                      <Button
                        key={folder}
                        variant="outline"
                        className="justify-start"
                        onClick={() => loadFiles(currentPrefix ? `${currentPrefix}/${folder}` : folder)}
                      >
                        <FolderOpen className="h-4 w-4 mr-2 text-yellow-500" />
                        {folder}
                        <ChevronRight className="h-4 w-4 ml-auto" />
                      </Button>
                    ))}
                  </div>
                )}

                {/* Files */}
                {files.files.length > 0 ? (
                  <div className="space-y-2">
                    {files.files.map((file) => (
                      <div key={file.key} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted">
                        <div className="flex items-center gap-3">
                          {getFileIcon(file.name)}
                          <div>
                            <div className="font-medium">{file.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {file.size_formatted}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Database className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No hay archivos en esta ubicación</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* BACKUP TAB */}
        <TabsContent value="backup" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Archive className="h-5 w-5" />
                Copias de Seguridad
              </CardTitle>
              <CardDescription>
                Crea manifiestos de backup de todos los archivos almacenados en S3
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={handleCreateBackup} disabled={creatingBackup}>
                {creatingBackup ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Archive className="h-4 w-4 mr-2" />
                )}
                Crear Backup Ahora
              </Button>

              {backups.length > 0 && (
                <div className="space-y-2 mt-4">
                  <h4 className="font-medium">Backups disponibles</h4>
                  {backups.map((backup, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Archive className="h-5 w-5 text-blue-500" />
                        <div>
                          <div className="font-medium">{backup.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(backup.date).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        <Download className="h-4 w-4 mr-1" /> Descargar
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* MAINTENANCE TAB */}
        <TabsContent value="maintenance" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Orphaned Files */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  Archivos Huérfanos
                </CardTitle>
                <CardDescription>
                  Archivos en S3 sin registro en BD o viceversa
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={handleFindOrphaned} variant="outline">
                  <Search className="h-4 w-4 mr-2" /> Analizar
                </Button>

                {orphanedFiles && (
                  <div className="space-y-3 pt-4">
                    <div className="p-3 bg-muted rounded-lg">
                      <div className="flex justify-between">
                        <span>En S3 sin BD:</span>
                        <Badge variant={orphanedFiles.orphaned_in_s3.count > 0 ? 'destructive' : 'default'}>
                          {orphanedFiles.orphaned_in_s3.count}
                        </Badge>
                      </div>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <div className="flex justify-between">
                        <span>En BD sin S3:</span>
                        <Badge variant={orphanedFiles.missing_in_s3.count > 0 ? 'destructive' : 'default'}>
                          {orphanedFiles.missing_in_s3.count}
                        </Badge>
                      </div>
                    </div>
                    
                    {orphanedFiles.orphaned_in_s3.count > 0 && (
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleCleanupOrphaned(true)}
                          disabled={cleaning}
                        >
                          Vista previa
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => handleCleanupOrphaned(false)}
                          disabled={cleaning}
                        >
                          {cleaning && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                          Limpiar
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Migration */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <HardDrive className="h-5 w-5 text-blue-500" />
                  Migración Local → S3
                </CardTitle>
                <CardDescription>
                  Migrar archivos almacenados localmente a S3
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {statistics && statistics.summary.local_files > 0 ? (
                  <>
                    <Alert>
                      <AlertDescription>
                        Hay <strong>{statistics.summary.local_files}</strong> archivos almacenados localmente que pueden migrarse a S3.
                      </AlertDescription>
                    </Alert>
                    <Button onClick={handleMigrate} disabled={migrating}>
                      {migrating ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Cloud className="h-4 w-4 mr-2" />
                      )}
                      Migrar 20 archivos
                    </Button>
                  </>
                ) : (
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      Todos los archivos están almacenados en S3.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
